import { useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';

import useUI from 'hooks/use-ui';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import { useRemoteThemeConfig } from 'src/hooks/use-remote-theme-config';
import {
  compareSmartTagNamesNaturally,
  filterSmartTagNamesByMatch,
  getSmartTagNamesFromProducts,
} from 'src/utils/helpers/smart-tag-filtering';
import type { SidebarCategory } from '../types';

type UseCustomSmartTagFilterForCategoryInput = {
  category?: SidebarCategory | null;
  onSearchPage?: boolean;
  onCollectionsPage?: boolean;
  onBrandsPage?: boolean;
  customCategory?: unknown;
};

type CustomSmartTagFilterForCategory = {
  smartTags: string[];
  title?: string;
};

const emptyResult: CustomSmartTagFilterForCategory = { smartTags: [] };

/**
 * Resolves the theme's custom smart-tag filter (if any) for the current top-level category list
 * page. Out of scope: search, collections, brands, custom categories, and subcategory pages —
 * only a true top-level category page (e.g. `/products/pre-rolls`) can show a custom smart-tag
 * filter, and only when the theme declares one for that exact category key.
 *
 * Options are derived from a bounded product sample scoped to this category, filtered to POS
 * active batch smart-tag names matching the config's `match` substrings, skipping the smartTags
 * filter itself so a selection never removes other options. If no product in the category
 * carries a matching smart tag, no filter renders at all.
 */
export function useCustomSmartTagFilterForCategory({
  category,
  onSearchPage,
  onCollectionsPage,
  onBrandsPage,
  customCategory,
}: UseCustomSmartTagFilterForCategoryInput): CustomSmartTagFilterForCategory {
  const UI = useUI();
  const isOnSubcategoryPage = useObserver(() => UI.onSubcategoryPage);
  const isTopLevelCategoryPage =
    Boolean(category) &&
    !onSearchPage &&
    !onCollectionsPage &&
    !onBrandsPage &&
    !customCategory &&
    !isOnSubcategoryPage;

  const { customSmartTagFilters } = useRemoteThemeConfig();
  const config = isTopLevelCategoryPage
    ? customSmartTagFilters.find((filter) => filter.category === category?.key)
    : undefined;

  const { queryParams } = useProductQueryParams();
  // Always sample page 1 of the category, independent of the URL's current page query param.
  // useDispensaryProducts falls back to `page || pageFromQueryParams`, so without an explicit
  // page:1 override here, this options sample would silently re-paginate along with the
  // product list -- once a user navigates past page 1 of a category with < perPage products,
  // the sample slice goes empty and the filter's tag options vanish (or shift, for larger
  // categories).
  const { products } = useDispensaryProducts({
    perPage: 500,
    page: 1,
    skip: !config,
    filters: { category: category?.value, skipSmartTagsFilter: true },
  });

  return useMemo(() => {
    if (!config) {
      return emptyResult;
    }

    const matchingTagNames = new Set(filterSmartTagNamesByMatch(getSmartTagNamesFromProducts(products), config.match));
    queryParams.smartTags.forEach((tag: string) => matchingTagNames.add(tag));

    const smartTags = Array.from(matchingTagNames).sort(compareSmartTagNamesNaturally);
    return smartTags.length ? { smartTags, title: config.title } : emptyResult;
  }, [config, products, queryParams.smartTags]);
}
